import { createReducer, type Reducer } from "@reduxjs/toolkit";

import { assert } from "../../../asserts/index.ts";
import type {
  AddTriggerAction,
  AddTriggerCaseAction,
  RemoveTriggerAction,
  RemoveTriggerCaseAction,
  TriggersByIdState,
  UpdateTriggerAction,
  UpdateTriggerCaseAction,
} from "../../../schemas/index.ts";
import {
  addTriggerAction,
  addTriggerCaseAction,
  removeTriggerAction,
  removeTriggerCaseAction,
  updateTriggerAction,
  updateTriggerCaseAction,
} from "./triggerActions.ts";
import type { TriggersReducerOptions } from "./triggersReducerOptions.ts";

export type TriggerEntryAction =
  | AddTriggerAction
  | RemoveTriggerAction
  | UpdateTriggerAction
  | AddTriggerCaseAction
  | UpdateTriggerCaseAction
  | RemoveTriggerCaseAction;

interface Options {
  notifyEvent?: TriggersReducerOptions["notifyEvent"];
}

export const triggersByIdReducerCreate = ({ notifyEvent }: Options): Reducer<TriggersByIdState, TriggerEntryAction> =>
  createReducer({} as TriggersByIdState, (builder) => {
    builder.addCase(addTriggerAction, (state, action) => {
      const { id, label, cases } = action.payload;
      assert(cases && cases.length > 0, "Cases are required when adding a trigger."); // schema validation would catch but extractor loses type

      if (notifyEvent) {
        notifyEvent({ type: "TRIGGER_ADDED", id, cases, label });
      }

      state[id] = {
        label,
        cases,
      };
    });

    builder.addCase(removeTriggerAction, (state, action) => {
      const { id: triggerId } = action.payload;
      if (notifyEvent) {
        const trigger = state[triggerId];
        if (trigger) {
          const { label } = trigger;
          // Create a shallow copy of cases to avoid passing Immer draft proxy
          const cases = trigger.cases.map((c) => ({ ...c }));
          notifyEvent({ type: "TRIGGER_REMOVED", id: triggerId, cases, label });
        }
      }
      delete state[triggerId];
    });

    builder.addCase(updateTriggerAction, (state, action) => {
      const { id: triggerId, label, cases } = action.payload;
      const trigger = state[triggerId];
      assert(trigger);
      trigger.label = label;
      if (cases) {
        trigger.cases = cases;
      }
      if (notifyEvent) {
        // Create a shallow copy of cases to avoid passing Immer draft proxy
        const casesSnapshot = trigger.cases.map((c) => ({ ...c }));
        notifyEvent({ type: "TRIGGER_UPDATED", id: triggerId, cases: casesSnapshot, label });
      }
    });

    builder.addCase(addTriggerCaseAction, (state, action) => {
      const { id: triggerId, eventType, where } = action.payload;
      const trigger = state[triggerId];
      assert(trigger, `Trigger not found: ${triggerId}`);
      trigger.cases.push({ eventType, where });
      if (notifyEvent) {
        // Create a shallow copy of cases to avoid passing Immer draft proxy
        const casesSnapshot = trigger.cases.map((c) => ({ ...c }));
        notifyEvent({ type: "TRIGGER_UPDATED", id: triggerId, cases: casesSnapshot, label: trigger.label });
      }
    });

    builder.addCase(updateTriggerCaseAction, (state, action) => {
      const { id: triggerId, index, eventType, where } = action.payload;
      const trigger = state[triggerId];
      assert(trigger, `Trigger not found: ${triggerId}`);
      assert(index >= 0 && index < trigger.cases.length, `Invalid case index: ${index}`);
      const triggerCase = trigger.cases[index];
      if (eventType !== undefined) {
        triggerCase.eventType = eventType;
      }
      if (where !== undefined) {
        triggerCase.where = where;
      }
      if (notifyEvent) {
        // Create a shallow copy of cases to avoid passing Immer draft proxy
        const casesSnapshot = trigger.cases.map((c) => ({ ...c }));
        notifyEvent({ type: "TRIGGER_UPDATED", id: triggerId, cases: casesSnapshot, label: trigger.label });
      }
    });

    builder.addCase(removeTriggerCaseAction, (state, action) => {
      const { id: triggerId, index } = action.payload;
      const trigger = state[triggerId];
      assert(trigger, `Trigger not found: ${triggerId}`);
      assert(index >= 0 && index < trigger.cases.length, `Invalid case index: ${index}`);
      trigger.cases.splice(index, 1);
      if (notifyEvent) {
        // Create a shallow copy of cases to avoid passing Immer draft proxy
        const casesSnapshot = trigger.cases.map((c) => ({ ...c }));
        notifyEvent({ type: "TRIGGER_UPDATED", id: triggerId, cases: casesSnapshot, label: trigger.label });
      }
    });
  });
